import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes, css } from "styled-components";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";

// 动画定义
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 样式组件
const StyledMessageContainer = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1010;
  pointer-events: none;
`;

const icon_svg_message = {
  success: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="18"
      viewBox="0 0 17 18"
      fill="none"
    >
      <rect y="0.5" width="17" height="17" rx="8.5" fill="#2E605A" />
      <rect
        x="12.1953"
        y="5.5"
        width="2"
        height="9"
        transform="rotate(45 12.1953 5.5)"
        fill="white"
      />
      <rect
        x="3"
        y="9.03552"
        width="2"
        height="6"
        transform="rotate(-45 3 9.03552)"
        fill="white"
      />
    </svg>
  ),
  error: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="18"
      viewBox="0 0 17 18"
      fill="none"
    >
      <rect y="0.5" width="17" height="17" rx="8.5" fill="#C41212" />
      <rect
        x="11.1172"
        y="5.25005"
        width="2"
        height="9"
        transform="rotate(45 11.1172 5.25005)"
        fill="white"
      />
      <rect
        x="12.5312"
        y="11.614"
        width="2"
        height="9"
        transform="rotate(135 12.5312 11.614)"
        fill="white"
      />
    </svg>
  ),
  warning: <ExclamationCircleOutlined />,
  info: <InfoCircleOutlined />,
};

const MessageItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  margin-bottom: 8px;
  border-radius: 6px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  background: #fff;
  border: 1px solid #d9d9d9;
  min-width: 300px;
  max-width: 400px;
  pointer-events: auto;
  animation: ${fadeIn} 0.3s ease-out;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
  border-radius: 8px;

  ${(props) =>
    props.isExiting &&
    css`
      animation: ${slideOut} 0.3s ease-in forwards;
    `}

  ${(props) => {
    // 如果提供了自定义样式，优先使用自定义样式
    if (props.customStyle) {
      return css`
        ${props.customStyle}
      `;
    }

    // 否则使用默认类型样式
    switch (props.type) {
      case "success":
        return css`
          border-color: #e1f2e2;
          background: #e1f2e2;
          color: #2e605a;
          //   .message-icon {
          //     color: #52c41a;
          //   }
        `;
      case "error":
        return css`
          border-color: #F8DFDF;
          background: #F8DFDF;
          color: #C41212;
        //   .message-icon {
        //     color: #ff4d4f;
        //   }
        `;
      case "warning":
        return css`
          border-color: #ffe58f;
          background: #fffbe6;
          .message-icon {
            color: #faad14;
          }
        `;
      case "info":
      default:
        return css`
          border-color: #91d5ff;
          background: #e6f7ff;
          .message-icon {
            color: #1890ff;
          }
        `;
    }
  }}
`;

const MessageContent = styled.div`
  flex: 1;
  margin-left: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.85);

  ${(props) =>
    props.contentStyle &&
    css`
      ${props.contentStyle}
    `}
`;

const MessageIcon = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;

  ${(props) =>
    props.iconStyle &&
    css`
      ${props.iconStyle}
    `}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  display: flex;
  align-items: center;
  transition: color 0.3s;

  &:hover {
    color: rgba(0, 0, 0, 0.75);
  }

  ${(props) =>
    props.closeButtonStyle &&
    css`
      ${props.closeButtonStyle}
    `}
`;

// 获取默认图标
const getDefaultIcon = (type) => {
  switch (type) {
    case "success":
      return icon_svg_message.success; // <CheckCircleOutlined />;
    case "error":
      return icon_svg_message.error; // <CloseCircleOutlined />;
    case "warning":
      return <ExclamationCircleOutlined />;
    case "info":
    default:
      return <InfoCircleOutlined />;
  }
};

// Message组件
const MessageList = ({ messages, onRemove }) => {
  return (
    <StyledMessageContainer>
      {messages.map((message) => (
        <MessageItem
          key={message.id}
          type={message.type}
          isExiting={message.isExiting}
          customStyle={message.style}
        >
          <MessageIcon className="message-icon" iconStyle={message.iconStyle}>
            {message.icon || getDefaultIcon(message.type)}
          </MessageIcon>
          <MessageContent contentStyle={message.contentStyle}>
            {message.content}
          </MessageContent>
          {/* <CloseButton
            onClick={() => onRemove(message.id)}
            closeButtonStyle={message.closeButtonStyle}
          >
            {message.closeIcon || <CloseOutlined />}
          </CloseButton> */}
        </MessageItem>
      ))}
    </StyledMessageContainer>
  );
};

// Message实例管理
let messageInstance = null;

class MessageManager {
  constructor() {
    this.messages = [];
    this.container = null;
  }

  createContainer() {
    if (!this.container) {
      this.container = document.createElement("div");
      this.container.id = "custom-message-container";
      document.body.appendChild(this.container);
    }
    return this.container;
  }

  addMessage(config) {
    const id = Date.now() + Math.random();
    const message = {
      id,
      ...config,
      duration: config.duration !== undefined ? config.duration : 3,
    };

    this.messages.push(message);
    this.render();

    // 自动移除
    if (message.duration > 0) {
      setTimeout(() => {
        this.removeMessage(id);
      }, message.duration * 1000);
    }

    return id;
  }

  removeMessage(id) {
    const index = this.messages.findIndex((msg) => msg.id === id);
    if (index > -1) {
      this.messages.splice(index, 1);
      this.render();
    }
  }

  render() {
    if (!this.container) {
      this.createContainer();
    }

    ReactDOM.render(
      <MessageList
        messages={this.messages}
        onRemove={this.removeMessage.bind(this)}
      />,
      this.container
    );
  }

  destroy() {
    if (this.container) {
      ReactDOM.unmountComponentAtNode(this.container);
      document.body.removeChild(this.container);
      this.container = null;
    }
  }
}

// 获取Message实例
const getMessageInstance = () => {
  if (!messageInstance) {
    messageInstance = new MessageManager();
  }
  return messageInstance;
};

// 导出Message方法
const message = {
  success(content, duration, options = {}) {
    return getMessageInstance().addMessage({
      type: "success",
      content,
      duration,
      ...options,
    });
  },

  error(content, duration, options = {}) {
    return getMessageInstance().addMessage({
      type: "error",
      content,
      duration,
      ...options,
    });
  },

  warning(content, duration, options = {}) {
    return getMessageInstance().addMessage({
      type: "warning",
      content,
      duration,
      ...options,
    });
  },

  info(content, duration, options = {}) {
    return getMessageInstance().addMessage({
      type: "info",
      content,
      duration,
      ...options,
    });
  },

  // 通用方法
  open(config) {
    return getMessageInstance().addMessage(config);
  },

  // 移除指定消息
  remove(id) {
    getMessageInstance().removeMessage(id);
  },

  // 移除所有消息
  destroy() {
    getMessageInstance().destroy();
    messageInstance = null;
  },
};

// 使用示例组件
const MessageExample = () => {
  const showMessage = (type) => {
    const messages = {
      success: "操作成功！",
      error: "操作失败，请重试。",
      warning: "警告信息",
      info: "这是一条信息提示",
    };

    message[type](messages[type]);
  };

  return (
    <div style={{ padding: "20px" }}>
      <h3>Message component example</h3>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          onClick={() => showMessage("success")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#52c41a",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          success message
        </button>
        <button
          onClick={() => showMessage("error")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#ff4d4f",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          error message
        </button>
        <button
          onClick={() => showMessage("warning")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#faad14",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          warning message
        </button>
        <button
          onClick={() => showMessage("info")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#1890ff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          info message
        </button>
      </div>
    </div>
  );
};

export default message;
export { MessageExample };
